/**
 * The long-move registry: every art's file (data/longMoves/<art>.ts) registers its lock routine and its ท่าใหญ่ here on
 * import; data/longMoves/index.ts imports them all. Checked on registration (beats in order inside the length, shares add
 * up to 1, one entry per move), so a bad table fails at load, not mid-bout. Data only.
 */
import type { LongKind, LongMoveDef } from './types';

const DEFS = new Map<string, LongMoveDef>();

export function registerLongMove(def: LongMoveDef): LongMoveDef {
  if (DEFS.has(def.skillId)) throw new Error(`long move registered twice: ${def.skillId}`);
  const b = def.beats;
  if (!b.length || b[0]!.at !== 0) throw new Error(`${def.skillId}: the first beat is at 0`);
  for (let i = 1; i < b.length; i++)
    if (!(b[i]!.at > b[i - 1]!.at)) throw new Error(`${def.skillId}: beats out of order at ${i}`);
  if (!(b[b.length - 1]!.at < def.len)) throw new Error(`${def.skillId}: a beat past the length`);
  const sum = b.reduce((t, x) => t + x.share, 0);
  if (Math.abs(sum - 1) > 1e-6) throw new Error(`${def.skillId}: shares add up to ${sum}`);
  if (def.kind === 'lock' && (def.catch === undefined || def.opener.type !== 'contact'))
    throw new Error(`${def.skillId}: a lock routine has a catch and a contact opener`);
  if (def.kind === 'ult' && (!def.whiff || def.opener.type === 'contact' || def.opener.tell <= 0))
    throw new Error(`${def.skillId}: an ult has a whiff, a tell and a field/ground/lunge/volley opener`);
  DEFS.set(def.skillId, def);
  return def;
}

/** The long move of a skill (null: none). */
export const longMoveOf = (skillId: string): LongMoveDef | null => DEFS.get(skillId) ?? null;

/** Every registered long move of a kind, in registration order. */
export const longMovesOf = (kind: LongKind): LongMoveDef[] =>
  [...DEFS.values()].filter((d) => d.kind === kind);

/** Every registered long move. */
export const allLongMoves = (): LongMoveDef[] => [...DEFS.values()];
